import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getOpenPositions, getClosedPositions, getPositionById } from '@/api/positions';
import { getPriceChanges, getPriceChangesForPeriod } from '@/api/marketData';

/**
 * Hook to get open positions
 */
export const useOpenPositions = (userId: string, enabled: boolean = true, exchangeCode?: string) => {
  return useQuery({
    queryKey: ['positions', 'open', userId, exchangeCode],
    queryFn: () => getOpenPositions(userId, exchangeCode),
    enabled: enabled && !!userId,
    staleTime: 10000, // 10 seconds
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

/**
 * Hook to get closed positions with pagination
 */
export const useClosedPositions = (userId: string, limit: number = 5, skip: number = 0, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['positions', 'closed', userId, limit, skip],
    queryFn: () => getClosedPositions(userId, limit, skip),
    enabled: enabled && !!userId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get closed positions with infinite scroll pagination
 */
export const useInfiniteClosedPositions = (userId: string, limit: number = 5, enabled: boolean = true, exchangeCode?: string) => {
  return useInfiniteQuery({
    queryKey: ['positions', 'closed', 'infinite', userId, limit, exchangeCode],
    queryFn: ({ pageParam = 0 }) => getClosedPositions(userId, limit, pageParam, exchangeCode),
    enabled: enabled && !!userId,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasMore) {
        return lastPage.pagination.skip + limit;
      }
      return undefined;
    },
    initialPageParam: 0,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get position by ID
 */
export const usePositionById = (userId: string, positionId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['positions', 'byId', userId, positionId],
    queryFn: () => getPositionById(userId, positionId),
    enabled: enabled && !!userId && !!positionId,
    staleTime: 10000, // 10 seconds
  });
};

/**
 * Hook to get price changes for a symbol
 */
export const usePriceChanges = (
  userId: string,
  symbol: string,
  timestamp: number,
  interval: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' = '1m',
  limit: number = 15,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['market-data', 'price-changes', userId, symbol, timestamp, interval, limit],
    queryFn: () => getPriceChanges(userId, symbol, timestamp, interval, limit),
    enabled: enabled && !!userId && !!symbol && !!timestamp,
    staleTime: 60000, // 1 minute
  });
};

/**
 * Hook to get price changes for a symbol within a period
 */
export const usePriceChangesForPeriod = (
  userId: string,
  symbol: string,
  startTime: number | null,
  endTime: number | null,
  interval: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' = '1m',
  padding: number = 10,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['market-data', 'price-changes-period', userId, symbol, startTime, endTime, interval, padding],
    queryFn: () => {
      if (!startTime || !endTime) {
        return Promise.resolve([]);
      }
      return getPriceChangesForPeriod(userId, symbol, startTime, endTime, interval, padding);
    },
    enabled: enabled && !!userId && !!symbol && !!startTime && !!endTime,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

